import { useId } from 'react'
import { useIntl } from 'react-intl'

import { type SupportBlocking as SupportBlockingValue, type SupportLine } from '@onr/schema'
import { MessageSquareIcon } from 'lucide-react'

import { Avatar, type AvatarProps } from '@/components/atoms/Avatar'
import { ChoiceButton } from '@/components/atoms/ChoiceButton'
import { EmptyState } from '@/components/atoms/EmptyState'
import { InlineError } from '@/components/atoms/InlineError'
import { Input } from '@/components/atoms/Input/Input.tsx'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/Select/Select.tsx'
import { SupportBlocking } from '@/components/molecules/SupportBlocking'
import { cn } from '@/lib/utils.ts'
import { AGENT_QUEUE_FILTERS, type AgentQueueFilter } from '@/state/support/agentQueue'

export type SupportQueueFilter = AgentQueueFilter
export type SupportEscouadeFilter = 'all' | 'me' | 'none'

const ESCOUADE_FILTERS = ['all', 'me', 'none'] as const satisfies ReadonlyArray<SupportEscouadeFilter>

export interface SupportAgentQueueRow {
  readonly number: number
  readonly meta: string
  readonly since: string
  readonly title: string
  readonly blocking: SupportBlockingValue
  readonly author?: string
  readonly waitingFor?: string
  readonly assignee: Pick<AvatarProps, 'tone' | 'initials' | 'label'>
  readonly unread: boolean
}

export interface SupportAgentQueueProps {
  readonly line: SupportLine
  readonly filter: SupportQueueFilter
  readonly counts: Readonly<Record<Exclude<SupportQueueFilter, 'settled'>, number>>
  readonly escouade: SupportEscouadeFilter
  readonly search: string
  readonly rows: ReadonlyArray<SupportAgentQueueRow>
  readonly selected?: number
  readonly state: 'loading' | 'ready' | 'unavailable'
  readonly onFilterChange: (filter: SupportQueueFilter) => void
  readonly onEscouadeChange: (escouade: SupportEscouadeFilter) => void
  readonly onSearchChange: (search: string) => void
  readonly onSelect: (number: number) => void
  readonly onRetry: () => void
}

export const SupportAgentQueue = ({
  line,
  filter,
  counts,
  escouade,
  search,
  rows,
  selected,
  state,
  onFilterChange,
  onEscouadeChange,
  onSearchChange,
  onSelect,
  onRetry,
}: SupportAgentQueueProps) => {
  const intl = useIntl()
  const titleId = useId()
  const escouadeId = useId()
  const searchId = useId()

  const filterLabel = (value: SupportQueueFilter) =>
    value === 'settled'
      ? intl.formatMessage({ id: 'soutien.queueFilter.settled' })
      : intl.formatMessage({ id: `soutien.queueFilter.${value}` }, { count: counts[value] })

  const body = (() => {
    if (state === 'unavailable') {
      return (
        <InlineError
          layout="centered"
          message={intl.formatMessage({ id: 'soutien.unavailable.message' })}
          retryLabel={intl.formatMessage({ id: 'soutien.unavailable.retry' })}
          onRetry={onRetry}
        />
      )
    }
    if (state === 'loading') {
      return <div className="flex-1" />
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          icon={<MessageSquareIcon aria-hidden className="size-8 stroke-[1.5] text-gray-500" />}
          message={intl.formatMessage({
            id: search.trim() === '' ? 'soutien.agent.queue.empty' : 'soutien.agent.queue.noMatch',
          })}
        />
      )
    }
    return (
      <nav aria-label={intl.formatMessage({ id: 'soutien.agent.queue.list' })} className="flex-1 overflow-y-auto">
        <ul>
          {rows.map((row) => {
            const current = row.number === selected
            return (
              <li key={row.number}>
                <button
                  type="button"
                  aria-current={current || undefined}
                  onClick={() => {
                    onSelect(row.number)
                  }}
                  className={cn(
                    'grid w-full cursor-pointer grid-cols-[10px_minmax(0,1fr)_auto] gap-x-2 gap-y-1.5 border-b border-gray-200 py-3.5 pr-4 pl-2.5 text-left hover:bg-gray-50',
                    current && 'bg-blue-100 shadow-[inset_0_0_0_2px_var(--color-blue-600)] hover:bg-blue-100'
                  )}>
                  {row.unread ? (
                    <span
                      role="img"
                      aria-label={intl.formatMessage({ id: 'soutien.requests.unread' })}
                      className="mt-[3px] size-2 rounded-full bg-blue-700"
                    />
                  ) : (
                    <span />
                  )}
                  <span className={cn('text-sm', row.unread ? 'text-gray-600' : 'text-gray-500')}>{row.meta}</span>
                  <span className={cn('text-right text-sm', row.unread ? 'text-gray-700' : 'text-gray-500')}>
                    {row.since}
                  </span>
                  <span />
                  <span className={cn('col-span-2 text-gray-900', row.unread && 'font-bold')}>{row.title}</span>
                  <span />
                  <span className="col-span-2 flex flex-wrap items-center gap-2.5 text-sm">
                    <SupportBlocking blocking={row.blocking} />
                    {row.author && <span className="text-gray-600">{row.author}</span>}
                    {row.waitingFor && <span className="text-gray-600">· {row.waitingFor}</span>}
                    <Avatar {...row.assignee} className="ml-auto" />
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </nav>
    )
  })()

  return (
    <section
      aria-labelledby={titleId}
      className="flex h-full w-[380px] max-w-full shrink-0 flex-col overflow-hidden rounded-[20px] bg-white">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 pt-4 pb-3">
        <h1 id={titleId} className="font-heading text-lg font-semibold text-gray-900">
          {intl.formatMessage({ id: 'soutien.agent.queue.title' })}
        </h1>
        <div
          role="group"
          aria-label={intl.formatMessage({ id: 'soutien.queueFilter.label' })}
          className="flex flex-wrap gap-1.5">
          {AGENT_QUEUE_FILTERS[line].map((value) => (
            <ChoiceButton
              key={value}
              shape="filter"
              pressed={filter === value}
              onClick={() => {
                onFilterChange(value)
              }}>
              {filterLabel(value)}
            </ChoiceButton>
          ))}
        </div>
        {filter === 'settled' && (
          <p className="text-sm text-gray-600">{intl.formatMessage({ id: 'soutien.queueFilter.settled.hint' })}</p>
        )}
        <div className="flex gap-2">
          {line === 'l1' && (
            <div className="flex w-[150px] shrink-0 flex-col gap-1">
              <label id={escouadeId} className="text-sm text-gray-600">
                {intl.formatMessage({ id: 'soutien.escouadeFilter.label' })}
              </label>
              <Select
                value={escouade}
                onValueChange={(value) => {
                  onEscouadeChange(value as SupportEscouadeFilter)
                }}>
                <SelectTrigger variant="field" aria-labelledby={escouadeId} className="h-8 bg-gray-100 text-gray-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ESCOUADE_FILTERS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {intl.formatMessage({ id: `soutien.escouadeFilter.${value}` })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <label htmlFor={searchId} className="text-sm text-gray-600">
              {intl.formatMessage({ id: 'soutien.search.label' })}
            </label>
            <Input
              id={searchId}
              type="search"
              value={search}
              placeholder={intl.formatMessage({ id: 'soutien.search.placeholder' })}
              className="h-8 rounded-[4px] border-gray-300 text-base text-gray-900 placeholder:text-gray-400"
              onChange={(event) => {
                onSearchChange(event.target.value)
              }}
            />
          </div>
        </div>
      </div>
      {body}
    </section>
  )
}
